import axios, { AxiosError, AxiosRequestConfig, isAxiosError } from 'axios';
import { getSession } from './getSession';
import { ExpectedError } from './createServerAction';
import { getQueryClient } from './queryClientSingleton';
import {
  destroyUserSesion,
  saveUserSession,
  setMaintenanceMode,
} from '../authentication/utils';
import { routes } from './routes';
import { redirect } from 'next/navigation';
import { logMessage } from './utils';
import { User } from '../authentication/types';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { useBoundStore } from './stores/store';
import { getBrowserPreferredLocale } from '@/i18n/actions';
import { detectClientLocale } from '@/i18n/utils';


const baseUrl = process.env.NEXT_PUBLIC_BASE_API_URL ?? '';

export interface ClientConfig extends Omit<AxiosRequestConfig, 'data' | 'url'> {
  body?: object;
  params?: Record<string, unknown>;
}

interface APISuccessResponse<T> {
  statusCode: 200;
  message: 'Success';
  data: T;
}

interface APIErrorResponse {
  statusCode: number;
  message: string;
  errorCode?: string;
  /** Per-error payload: validation rows, `minZoom`, the offending `file`… */
  data?: unknown;
  status: 'error';
  path: string;
  timestamp: string;
  stack?: string;
}

const headers: Record<string, string> = {
  platform: 'orth_web',
};

const api = axios.create({
  baseURL: baseUrl,
  headers,
  timeout: process.env.NEXT_PUBLIC_API_TIMEOUT
    ? parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT)
    : 600000,
});

const isServer = () => typeof window === 'undefined';

const navigate = (route: string) => {
  if (!isServer()) {
    window.location.href = route;
  } else {
    redirect(route);
  }
};

// Cookie wins over the browser preference, matching the request interceptor.
const getClientLocale = () => {
  const cookieLocale = document.cookie
    .split('; ')
    .find((row) => row.startsWith('locale='))
    ?.split('=')[1];
  return cookieLocale ?? detectClientLocale();
};

const isAPIError = (
  error: AxiosError
): error is AxiosError<APIErrorResponse> => {
  const data = error.response?.data;
  return !!data && typeof data === 'object' && 'message' in data;
};

export const isInvalidToken = (error?: string) => {
  return (
    error === 'TOKEN_REVOKED' ||
    error === 'INVALID_TOKEN_OR_EXPIRED' ||
    error === 'NO_TOKEN_PROVIDED' ||
    error === 'INVALID_TOKEN#001'
  );
};

const shouldRefresh = (error: AxiosError<APIErrorResponse>) => {
  return isInvalidToken(error.response?.data.errorCode);
};

export const refreshAuthLogic = async (failedRequest?: {
  response: { config: { headers: { Authorization: string } } };
}) => {
  if (!isServer()) useBoundStore.getState().setIsRefreshingToken(true);
  const { accessToken } = await getSession();
  const { refreshToken } = await getSession('refreshToken');
  try {
    const { data: res } = await axios.post<{
      data: { user: User; accessToken: string };
    }>(
      `${baseUrl}/api/v1/auth/refresh-token`,
      { accessToken, refreshToken },
      {
        headers,
      }
    );
    await saveUserSession(res.data);
    if (!isServer()) {
      useBoundStore.getState().setAccessToken(res.data.accessToken);
      // Ensure profile data is refreshed after token renewal.
      const queryClient = getQueryClient();
      queryClient?.invalidateQueries({ queryKey: ['userProfile'] });
    }
    if (failedRequest)
      failedRequest.response.config.headers.Authorization = `Bearer ${res.data.accessToken}`;
    return Promise.resolve();
  } catch (error) {
    await destroyUserSesion();
    navigate(routes.login);
    return Promise.reject(error as AxiosError);
  } finally {
    if (!isServer()) useBoundStore.getState().setIsRefreshingToken(false);
  }
};

api.interceptors.request.use(async (request) => {
  let accessToken: string | undefined = '';
  let locale: string | undefined = '';
  if (isServer()) {
    const { cookies } = await import('next/headers');
    const cookieLocale = (await cookies()).get('locale')?.value;
    const browserLocale = await getBrowserPreferredLocale();
    locale = cookieLocale ?? browserLocale;
    accessToken = (await getSession()).accessToken;
  } else {
    locale = getClientLocale();
    accessToken = useBoundStore.getState().accessToken;
  }
  if (accessToken) {
    request.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (locale) request.headers['Accept-Language'] = locale;
  return request;
});

// unwraps and rethrows errors in case of responseType of `blob`, to be handled by the next interceptor.
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    if (isAxiosError(error) && error.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      return Promise.reject({
        ...error,
        response: {
          ...error.response,
          data: JSON.parse(text),
        },
      });
    }

    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isAxiosError(error) && isAPIError(error)) {
      const apiError = error.response?.data;

      // Platform-wide maintenance and per-account migrations both park the
      // user on the maintenance page until the API starts answering again.
      if (
        apiError?.statusCode === 503 &&
        (apiError?.errorCode === 'MAINTENANCE_MODE' ||
          apiError?.errorCode === 'ACCOUNT_MIGRATION_IN_PROGRESS')
      ) {
        await setMaintenanceMode(true).catch(() => {});
        navigate(routes.maintenance);
        return;
      }

      if (apiError?.statusCode === 401 && apiError.errorCode === 'UNAUTHORIZED') {
        return Promise.reject(
          new ExpectedError(apiError.errorCode, apiError.statusCode)
        );
      }

      if (
        apiError?.errorCode === 'INVALID_REFRESH_TOKEN' ||
        (apiError?.statusCode === 401 && !shouldRefresh(error))
      ) {
        await destroyUserSesion();
        if (!isServer()) useBoundStore.getState().clearAccessToken();
        navigate(routes.login);
      } else if (!shouldRefresh(error)) {
        const path = error.config?.url;
        const requestBody = error.config?.data;
        const message = apiError?.errorCode ?? apiError?.message;

        const errorCodes = [500, 404, 502, 503, 504, 401, 403];
        const isErrorCode = apiError?.statusCode
          ? errorCodes.includes(apiError.statusCode)
          : false;

        const { level, prefix } = isErrorCode
          ? { level: 'error', prefix: `API Error` }
          : apiError?.statusCode === 400
          ? { level: 'warning', prefix: 'API warning' }
          : { level: 'info', prefix: `API Info` };

        let sanitizedBody: Record<string, unknown> | string = {};
        if (requestBody instanceof FormData) {
          const formDataEntries: Record<string, unknown> = {};
          for (const [key, value] of requestBody.entries()) {
            if (key.toLowerCase() !== 'password')
              formDataEntries[key] =
                value instanceof File ? `[File: ${value.name}]` : value;
          }
          sanitizedBody = formDataEntries;
        } else if (requestBody && typeof requestBody === 'object') {
          sanitizedBody = { ...requestBody, password: undefined };
        }
        logMessage(level, `${prefix} : ${message} at path: ${path}`, {
          requestBody: JSON.stringify(sanitizedBody),
          requestheaders: JSON.stringify(error.config?.headers ?? {}),
          path,
          response: JSON.stringify(apiError ?? {}),
        });
        return Promise.reject(
          new ExpectedError(message as string, apiError?.statusCode, {
            errorCode: apiError?.errorCode,
            data: (apiError as { data?: unknown })?.data,
          })
        );
      }
    }
    if (!shouldRefresh(error))
      logMessage('error', `Network error`, {
        rawError: JSON.stringify(error),
        path: error.config?.url,
      });
    // return rejected promise to be handled by the next interceptor
    return Promise.reject(error);
  }
);
createAuthRefreshInterceptor(api, refreshAuthLogic, {
  shouldRefresh: (error) => {
    if (isAPIError(error)) return shouldRefresh(error);
    return false;
  },
});

export default api;

export async function apiFetch<T = unknown>(
  endpoint: string,
  { body, params, ...customConfig }: ClientConfig = {}
): Promise<T> {
  const method =
    customConfig.method ?? (body ? ('POST' as const) : ('GET' as const));

  const serializedParams: Record<string, unknown> = {};
  if (params)
    Object.keys(params).forEach((key) => {
      if (params[key] !== '') serializedParams[key] = params[key];
    });

  const response = await api.request<APISuccessResponse<T>>({
    url: endpoint,
    method,
    data: body,
    params: serializedParams,
    ...customConfig,
  });


  // if(endpoint.indexOf('profile') !== -1 || endpoint.indexOf('team-members') !== -1){
  //   logMessage('info', 'API Response', {
  //     info: {
  //       response: JSON.stringify(response.data),
  //       endpoint,
  //       method,
  //       body: JSON.stringify(body),
  //       params: JSON.stringify(serializedParams),
  //       ...customConfig,
  //     },
  //   });
  // }
  return response.data.data;
}

/*
export async function apiFetch<T = unknown>(
  endpoint: string,
  { body, params, ...customConfig }: ClientConfig
) {
  const isServer = typeof window === 'undefined';

  const platform = 'orth_web';

  // This is intentionally outside makeRequest so we can log it later,
  // just like in the original fetch-based implementation.
  const headers: Record<string, string> = {
    platform,
  };

  const makeRequest = async (
    token?: string
  ): Promise<AxiosResponse<APISuccessResponse<T> | APIErrorResponse>> => {
    // Auth + locale headers
    if (isServer) {
      const { accessToken } = await getSession();
      const browserLocale = await getBrowserPreferredLocale();
      const { cookies } = await import('next/headers');
      const nextintlLocale = (await cookies()).get('NEXT_LOCALE')?.value;
      const locale = nextintlLocale ?? browserLocale;
      if (token || accessToken)
        headers.Authorization = `Bearer ${token ?? accessToken}`;
      if (locale) headers['Accept-Language'] = locale;
    } else {
      const store = useBoundStore.getState();
      if (token || store.accessToken)
        headers.Authorization = `Bearer ${token ?? store.accessToken}`;
      const cookieLocale = document.cookie
        .split('; ')
        .find((row) => row.startsWith('NEXT_LOCALE='))
        ?.split('=')[1];
      const browserLocale = detectClientLocale();
      const locale = cookieLocale ? cookieLocale : browserLocale;
      if (locale) headers['Accept-Language'] = locale;
    }

    const query = buildQueryParams(params);

    const mergedHeaders = {
      ...headers,
      ...(customConfig.headers as Record<string, string> | undefined),
    };

    const method =
      customConfig.method ?? (body ? ('POST' as const) : ('GET' as const));

    return axios.request<APISuccessResponse<T> | APIErrorResponse>({
      url: `${baseUrl}${endpoint}${query}`,
      method,
      data: body,
      timeout: process.env.NEXT_PUBLIC_API_TIMEOUT
        ? parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT)
        : 600000,
      // Let axios throw only on true network errors; for HTTP errors we
      // want to control the flow with handleResponse:
      validateStatus: () => true,
      ...customConfig,
      headers: mergedHeaders,
    });
  };

  const handleResponse = (
    res: AxiosResponse<APISuccessResponse<T> | APIErrorResponse>
  ): T => {
    const json = res.data;

    if (res.status >= 200 && res.status < 300) {
      return (json as APISuccessResponse<T>).data;
    }

    throw json as APIErrorResponse;
  };

  try {
    const res = await makeRequest();
    return handleResponse(res);
  } catch (err: unknown) {
    let e: APIErrorResponse | undefined;
    console.log(err);
    if (isAxiosError(err)) {
      e = err.response?.data as APIErrorResponse | undefined;
    } else {
      e = err as APIErrorResponse;
    }

    // If we don't have a structured APIErrorResponse, treat as unexpected
    if (!e) {
      logMessage('error', `API Unexpected error at ${endpoint}`, {
        rawError: JSON.stringify(err),
        path: endpoint,
      });

      // For unexpected errors, don't include statusCode (will retry)
      throw new ExpectedError('Unexpected Error');
    }

    if (
      e.errorCode === 'TOKEN_REVOKED' ||
      e.errorCode === 'INVALID_TOKEN_OR_EXPIRED' ||
      e.errorCode === 'NO_TOKEN_PROVIDED'
    ) {
      const newToken = await refreshToken();

      if (newToken) {
        if (!isServer) useBoundStore.getState().setAccessToken(newToken);
        if (!isServer && e.errorCode === 'TOKEN_REVOKED') {
          useBoundStore.getState().setAccessToken(newToken);

          const queryClient = getQueryClient();
          // Ensure profile data is refreshed after token renewal.
          queryClient?.invalidateQueries({ queryKey: ['userProfile'] });
        }

        const retryRes = await makeRequest(newToken);
        return handleResponse(retryRes);
      } else {
        await destroyUserSesion();
        if (!isServer) useBoundStore.getState().clearAccessToken();

        navigateToLogin(isServer);
        // navigateToLogin will redirect; but just in case:
        throw new ExpectedError('Session expired', 401);
      }
    }

    if (e.errorCode === 'INVALID_REFRESH_TOKEN') {
      await destroyUserSesion();
      navigateToLogin(isServer);
      throw new ExpectedError('Session expired', 401);
    }

    let message = 'Unexpected Error';
    message = e.errorCode ?? e.message ?? message;

    const errorCodes = [500, 404, 502, 503, 504];
    const isErrorCode = errorCodes.indexOf(e.statusCode) !== -1;

    const { level, prefix } = isErrorCode
      ? { level: 'error', prefix: `API Error` }
      : e.statusCode === 400
      ? { level: 'warning', prefix: 'API warning' }
      : { level: 'info', prefix: `API Info` };

    logMessage(level, `${prefix} : ${message} at path: ${e.path}`, {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      requestBody: JSON.stringify({ ...body, password: undefined } ?? {}),
      requestheaders: JSON.stringify(headers ?? {}),
      path: e.path,
      response: JSON.stringify(e ?? {}),
    });

    throw new ExpectedError(message, e.statusCode);
  }
}
*/
